import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { appsAPI, contactAPI, settingsAPI } from '../../services/api'
import { formatEuro, settingsToMap, toNumber } from '../../utils/format'
import AppIcon from '../../components/public/AppIcon'

import { logger } from '@/utils/logger';

/**
 * Texte, die im Backend über System-Settings überschrieben werden können
 * (Admin → Einstellungen → Weitere Einstellungen). Fehlt ein Schlüssel,
 * greift der Standardtext hier.
 */
const HERO_DEFAULTS = {
  home_hero_kicker: '// werkstatt-connect.de/portal',
  home_hero_title: 'Eine Plattform.\nAlle Apps für Ihre Werkstatt.',
  home_hero_subtitle:
    'Werkstatt-Connect bündelt Reifenhotel, Öldaten, Terminbuchung, Fahrzeugverleih und Zeiterfassung in einem gemeinsamen Kundenportal — API-first aufgebaut.',
  home_hero_badges: 'API-first, DSGVO-konform, zentrale Kundenverwaltung',
  home_cta_title: 'Bereit, Ihre Werkstatt zu verbinden?',
  home_cta_text:
    'Wir richten Ihren ersten Tenant in der Regel innerhalb eines Werktags ein.',
}

const FEATURES = [
  {
    title: 'API-first Architektur',
    text: 'Jede App spricht dasselbe Protokoll: ein Webhook-Vertrag, ein Tenant-Modell, eine zentrale Kundenverwaltung im Portal.',
    path: 'M8 21h8M12 17v4M6 3h12l-1 8a5 5 0 0 1-10 0L6 3z',
  },
  {
    title: 'Sicherheit als Standard',
    text: 'JWT-Sessions, 2FA, Login-Audit und CSRF-Schutz sind Teil jeder App — nicht nachgerüstet.',
    path: 'M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z',
  },
  {
    title: 'Schlanke Performance',
    text: 'FastAPI-Backends, React-Frontends, kurze Antwortzeiten — auf jedem Gerät in der Werkstatt.',
    path: 'M13 3 4 14h6l-1 7 9-11h-6l1-7z',
  },
]

const STEPS = [
  {
    no: '01',
    title: 'Verbinden',
    text: 'Sie wählen Ihre Apps im Portal, wir provisionieren den Tenant automatisch per Webhook.',
  },
  {
    no: '02',
    title: 'Konfigurieren',
    text: 'Tarif, Logo und Nutzer richten Sie selbst ein — pro App, ohne Rücksprache mit uns.',
  },
  {
    no: '03',
    title: 'Automatisieren',
    text: 'Rechnungen, Erinnerungen und Statusmeldungen laufen im Hintergrund weiter.',
  },
]

const EMPTY_FORM = { name: '', email: '', phone: '', subject: '', message: '' }

export default function Home() {
  const [apps, setApps] = useState([])
  const [texts, setTexts] = useState(HERO_DEFAULTS)
  const [contact, setContact] = useState({ email: '', phone: '' })

  const [formData, setFormData] = useState(EMPTY_FORM)
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [formError, setFormError] = useState('')

  useEffect(() => {
    loadApps()
    loadSettings()
  }, [])

  const loadApps = async () => {
    try {
      const response = await appsAPI.getAll()
      setApps(response.data)
    } catch (error) {
      logger.error('Error loading apps:', error)
    }
  }

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.getPublic()
      const map = settingsToMap(response.data)

      // Nur nicht-leere Werte aus dem Backend übernehmen
      const overrides = {}
      Object.keys(HERO_DEFAULTS).forEach(key => {
        if (map[key]) overrides[key] = map[key]
      })
      setTexts({ ...HERO_DEFAULTS, ...overrides })

      setContact({
        email: map.contact_email || map.footer_contact_email || '',
        phone: map.contact_phone || map.footer_contact_phone || '',
      })
    } catch (error) {
      logger.error('Error loading settings:', error)
    }
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormError('')
    setSent(false)
    setSending(true)

    try {
      await contactAPI.send(formData)
      setSent(true)
      setFormData(EMPTY_FORM)
    } catch (err) {
      setFormError(
        'Fehler beim Senden der Nachricht. Bitte versuchen Sie es später erneut.'
      )
      logger.error('Contact form error:', err)
    } finally {
      setSending(false)
    }
  }

  const badges = texts.home_hero_badges
    .split(',')
    .map(b => b.trim())
    .filter(Boolean)

  return (
    <div>
      {/* Hero */}
      <section
        className="wc-container wc-hero-grid"
        style={{
          padding: '88px 32px 64px',
          display: 'grid',
          gridTemplateColumns: '1.05fr 0.95fr',
          gap: 56,
          alignItems: 'center',
        }}
      >
        <div>
          <div
            className="wc-mono"
            style={{
              fontSize: 12,
              letterSpacing: '.06em',
              color: 'var(--wc-accent-300)',
              marginBottom: 18,
            }}
          >
            {texts.home_hero_kicker}
          </div>
          <h1 style={{ fontSize: 52, lineHeight: 1.08, margin: '0 0 20px', maxWidth: 620 }}>
            {texts.home_hero_title.split('\n').map((line, i) => (
              <span key={i} style={{ display: 'block' }}>
                {line}
              </span>
            ))}
          </h1>
          <p style={{ fontSize: 17, opacity: 0.8, maxWidth: 480, margin: '0 0 28px' }}>
            {texts.home_hero_subtitle}
          </p>
          <div style={{ display: 'flex', gap: 12, marginBottom: 32, flexWrap: 'wrap' }}>
            <Link to="/apps" className="wc-btn wc-btn-primary wc-btn-lg">
              Apps entdecken
            </Link>
            <a href="#kontakt" className="wc-btn wc-btn-secondary wc-btn-lg">
              Demo anfragen
            </a>
          </div>
          {badges.length > 0 && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {badges.map(badge => (
                <span key={badge} className="wc-tag wc-tag-outline">
                  {badge}
                </span>
              ))}
            </div>
          )}
        </div>

        <div
          className="wc-card wc-elev-md"
          style={{
            padding: 0,
            overflow: 'hidden',
            border: '1px solid var(--wc-divider)',
          }}
          aria-hidden="true"
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 16px',
              borderBottom: '1px solid var(--wc-divider)',
            }}
          >
            {[0, 1, 2].map(i => (
              <span
                key={i}
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: 'var(--wc-neutral-700)',
                  display: 'inline-block',
                }}
              />
            ))}
            <span className="wc-mono wc-muted" style={{ marginLeft: 8, fontSize: 12 }}>
              provision.sh
            </span>
          </div>
          <div
            className="wc-mono"
            style={{ padding: '22px 20px', fontSize: 13, lineHeight: 1.8 }}
          >
            <div>
              <span style={{ color: 'var(--wc-accent-300)' }}>POST</span>{' '}
              /api/v1/apps/tirevault/provision
            </div>
            <div style={{ opacity: 0.55 }}>{'{'}</div>
            <div style={{ paddingLeft: 16 }}>
              "tenant": <span style={{ color: 'var(--wc-accent)' }}>"meyer-kfz"</span>,
            </div>
            <div style={{ paddingLeft: 16 }}>
              "plan": <span style={{ color: 'var(--wc-accent)' }}>"pro"</span>
            </div>
            <div style={{ opacity: 0.55 }}>{'}'}</div>
            <div style={{ marginTop: 14, color: 'var(--wc-accent-300)' }}>
              200 OK{' '}
              <span className="wc-muted">· tenant provisioned in 340ms</span>
            </div>
            <div className="wc-muted">↳ Zugriff für meyer-kfz im Portal freigeschaltet</div>
          </div>
        </div>
      </section>

      <div className="wc-container">
        <div className="wc-hr" style={{ margin: 0 }}></div>
      </div>

      {/* Vorteile */}
      <section className="wc-container" style={{ padding: '72px 32px' }}>
        <p className="wc-kicker">Architektur</p>
        <h2 style={{ maxWidth: 520, marginBottom: 44 }}>
          Warum Werkstätten auf Werkstatt-Connect setzen
        </h2>
        <div className="wc-grid-3">
          {FEATURES.map(feature => (
            <div key={feature.title} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--wc-accent)"
                strokeWidth="1.5"
                aria-hidden="true"
              >
                <path d={feature.path} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <h3 style={{ marginTop: 6, marginBottom: 0 }}>{feature.title}</h3>
              <p className="wc-muted" style={{ fontSize: 14, margin: 0 }}>
                {feature.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Apps — Inhalte, Bilder und Preise kommen aus dem Backend */}
      <section id="apps" className="wc-container" style={{ padding: '72px 32px' }}>
        <p className="wc-kicker">Apps</p>
        <h2 style={{ maxWidth: 560, marginBottom: 12 }}>Alle Apps, ein Zugang</h2>
        <p className="wc-muted" style={{ maxWidth: 560, marginBottom: 44 }}>
          Jede App einzeln buchbar, alle über ein Kundenportal verwaltet.
        </p>

        <div className="wc-grid-3" style={{ gap: 20 }}>
          {apps.map(app => (
            <div key={app.id} className="wc-card wc-elev-sm wc-card-hover">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <AppIcon app={app} />
                {app.is_featured && (
                  <span className="wc-tag wc-tag-accent">Beliebt</span>
                )}
              </div>
              <h3 className="wc-card-title" style={{ marginTop: 4, marginBottom: 0 }}>
                {app.name}
              </h3>
              <p className="wc-card-body">{app.short_description}</p>
              {toNumber(app.price_monthly) > 0 && (
                <p className="wc-mono wc-muted" style={{ fontSize: 12, margin: 0 }}>
                  ab {formatEuro(app.price_monthly)} / Monat
                </p>
              )}
              <Link
                to={`/apps/${app.slug}`}
                className="wc-btn wc-btn-ghost"
                style={{ alignSelf: 'flex-start', marginTop: 4 }}
              >
                Mehr erfahren →
              </Link>
            </div>
          ))}

          <div
            className="wc-card wc-elev-sm"
            style={{
              justifyContent: 'center',
              alignItems: 'flex-start',
              border: '1px solid var(--wc-divider)',
              background: 'transparent',
            }}
          >
            <h3 className="wc-card-title" style={{ marginBottom: 0 }}>
              Alle Apps im Detail
            </h3>
            <p className="wc-card-body" style={{ flex: 'none' }}>
              Preise, Funktionsumfang und Integrationen im Vergleich.
            </p>
            <Link
              to="/apps"
              className="wc-btn wc-btn-primary"
              style={{ alignSelf: 'flex-start', marginTop: 4 }}
            >
              Zur Übersicht →
            </Link>
          </div>
        </div>
      </section>

      {/* Onboarding */}
      <section id="plattform" className="wc-container" style={{ padding: '72px 32px' }}>
        <p className="wc-kicker">Onboarding</p>
        <h2 style={{ maxWidth: 520, marginBottom: 44 }}>
          Vom Vertrag zur laufenden App in drei Schritten
        </h2>
        <div className="wc-grid-3" style={{ gap: 32 }}>
          {STEPS.map(step => (
            <div key={step.no}>
              <div
                className="wc-mono"
                style={{ color: 'var(--wc-accent)', fontSize: 13, marginBottom: 10 }}
              >
                {step.no}
              </div>
              <h3 style={{ marginBottom: 8 }}>{step.title}</h3>
              <p className="wc-muted" style={{ fontSize: 14, margin: 0 }}>
                {step.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA-Band */}
      <section
        style={{
          background: 'var(--wc-section)',
          backgroundImage:
            'radial-gradient(circle at 15% 20%, var(--wc-section-glow), transparent 60%)',
          padding: '64px 32px',
          marginTop: 8,
        }}
      >
        <div
          className="wc-container"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 24,
            flexWrap: 'wrap',
            padding: 0,
          }}
        >
          <div>
            <h2 style={{ marginBottom: 8 }}>{texts.home_cta_title}</h2>
            <p style={{ opacity: 0.8, margin: 0, maxWidth: 480 }}>
              {texts.home_cta_text}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <a
              href="#kontakt"
              className="wc-btn wc-btn-primary wc-btn-lg"
              style={{ background: 'color-mix(in srgb, var(--wc-bg) 40%, transparent)' }}
            >
              Demo anfragen
            </a>
            <Link to="/apps" className="wc-btn wc-btn-secondary wc-btn-lg">
              Apps ansehen
            </Link>
          </div>
        </div>
      </section>

      {/* Kontakt — läuft weiterhin ins Backend-Postfach */}
      <section
        id="kontakt"
        className="wc-container wc-split-grid"
        style={{
          padding: '72px 32px',
          display: 'grid',
          gridTemplateColumns: '0.9fr 1.1fr',
          gap: 48,
        }}
      >
        <div>
          <p className="wc-kicker">Kontakt</p>
          <h2 style={{ maxWidth: 380, marginBottom: 12 }}>
            Fragen zu einer App oder einem Tarif?
          </h2>
          <p className="wc-muted" style={{ maxWidth: 380 }}>
            Schreiben Sie uns — wir melden uns in der Regel innerhalb eines
            Werktags. Anfragen laufen direkt in unser Support-Postfach im Portal.
          </p>
          <div className="wc-muted" style={{ fontSize: 14, marginTop: 24 }}>
            {contact.email && (
              <div>
                <a href={`mailto:${contact.email}`} className="wc-footer-link">
                  {contact.email}
                </a>
              </div>
            )}
            {contact.phone && <div>{contact.phone}</div>}
          </div>
        </div>

        <form className="wc-card wc-elev-sm" style={{ gap: 16 }} onSubmit={handleSubmit}>
          {sent && (
            <div className="wc-alert wc-alert-success" role="status">
              Vielen Dank für Ihre Nachricht! Wir melden uns schnellstmöglich bei Ihnen.
            </div>
          )}
          {formError && (
            <div className="wc-alert wc-alert-error" role="alert">
              {formError}
            </div>
          )}

          <div className="wc-field">
            <label htmlFor="home-name">Name *</label>
            <input
              id="home-name"
              name="name"
              className="wc-input"
              type="text"
              placeholder="Ihr Name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="wc-field">
            <label htmlFor="home-email">E-Mail *</label>
            <input
              id="home-email"
              name="email"
              className="wc-input"
              type="email"
              placeholder="name@werkstatt.de"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className="wc-field">
            <label htmlFor="home-subject">Betrifft *</label>
            <input
              id="home-subject"
              name="subject"
              className="wc-input"
              type="text"
              placeholder="z. B. TireVault, Preise, Support"
              value={formData.subject}
              onChange={handleChange}
              required
            />
          </div>
          <div className="wc-field">
            <label htmlFor="home-message">Nachricht *</label>
            <textarea
              id="home-message"
              name="message"
              className="wc-input"
              placeholder="Wie können wir helfen?"
              value={formData.message}
              onChange={handleChange}
              required
            />
          </div>
          <button
            type="submit"
            className="wc-btn wc-btn-primary wc-btn-block"
            disabled={sending}
          >
            {sending ? 'Senden…' : 'Anfrage senden'}
          </button>
        </form>
      </section>
    </div>
  )
}
